import { Outlet } from 'react-router-dom'

import { Box, Button, useToggle } from '@factorialco/deprecated-design-system'
import { AvailableModules, Features } from 'generated/featureDefinitions'
import { CardsPageDocument } from 'generated/resources/graphql'

import Upgrade from 'modules/core/components/Upgrade'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import AccountOnboardingButton from 'modules/banking/components/AccountOnboarding/Button'
import { CardsDataTable } from 'modules/banking/components/CardsDataTable'
import { DefaultNotification } from 'modules/expenses/components/_Notification'
import DeadlineBlock from 'modules/expenses/components/cards/CardsPageBlocks/DeadlineBlock'
import { CtaDescription } from 'modules/expenses/components/common/CardsHero'
import CardsLandingPage from 'modules/expenses/components/common/CardsLandingPage'
import { usePhysicalCardsCreationLimits } from 'modules/expenses/hooks/useCardsCreationLimits'
import { missingFeatureRequestReason } from 'modules/finance/lib/missingFeatureRequestReason'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { UpsellingKit } from 'modules/upselling_kit/components'

const CardsPage = () => {
  const [isOpen, toggleOpen] = useToggle(false)
  const {
    access: { locale },
    company,
  } = useCurrentSession()

  const { data, loading, error } = useQuery(CardsPageDocument, {
    variables: {
      companyId: company.id,
      featureIds: [Features.expenses_physical_cards, Features.expenses_core],
    },
  })
  const { physicalCardsAvailability } = usePhysicalCardsCreationLimits(
    data?.companies.featureAvailabilitiesConnection.nodes.find(
      (feature) => feature.id === Features.expenses_physical_cards
    ) ?? {
      available: false,
      id: Features.expenses_physical_cards,
      _internalType: 'feature',
    }
  )
  const canAddCard = data?.companies.featureAvailabilitiesConnection.nodes.find(
    (feature) => feature.id === Features.expenses_core
  )?.available
  const cardholders = data?.banking.cardholders ?? []
  const companyId = company.id
  const currentCompany = data?.apiCore?.currentsConnection?.nodes[0]?.company
  const mainLegalEntity = currentCompany?.legalEntities.find((le) => le.main)
  const onboarding = mainLegalEntity?.bankingOnboardings[0]
  const hasFinishedAnyOnboarding = currentCompany?.legalEntities.some((le) =>
    le.bankingOnboardings?.some((o) => o.status === 'completed')
  )
  let ctaDescription: CtaDescription | undefined = undefined

  // If the company has finished an onboarding with any other legal entity, we don't want to show the CTA, otherwise
  // they can't create cards.
  if (
    !hasFinishedAnyOnboarding &&
    (!onboarding || onboarding.status == 'pending' || onboarding.status == 'missing_information')
  ) {
    ctaDescription = onboarding ? CtaDescription.COMPLETE_ACCOUNT : CtaDescription.CREATE_ACCOUNT
  }

  const account = mainLegalEntity?.bankingAccounts[0]
  const bankAccount = account?.bankAccountsConnection?.nodes[0]

  const Content = () => {
    if (!hasFinishedAnyOnboarding && onboarding?.deadline) {
      return (
        <>
          <Box width="full" maxWidth="full">
            <DefaultNotification
              onboardingStatus={onboarding.status}
              onboardingDeadline={onboarding.deadline}
              bankAccount={bankAccount}
              bankAddress={account?.bankAddress}
              locale={locale}
            />
          </Box>
          <DeadlineBlock
            button={
              <AccountOnboardingButton
                source="expenses"
                label={i18n.t('expenses_v2.cards.blocks.deadline.action_button')}
                trackingEvent={trackingEvents.expenses.goToAccountSettings}
              />
            }
            currentDeadline={onboarding.deadline}
          />
        </>
      )
    }

    if (ctaDescription) {
      return <CardsLandingPage ctaDescription={ctaDescription} />
    }

    return (
      <>
        <Box width="full" maxWidth="full">
          {!!physicalCardsAvailability?.warningMessage && (
            <>
              <Upgrade.Banner
                message={physicalCardsAvailability.warningMessage}
                button={
                  <Button
                    type="upgrade"
                    onClick={() => {
                      track(trackingEvents.expenses.goToRequestInformation, {
                        section: 'upgrade_banner',
                      })
                      toggleOpen(true)()
                    }}
                  >
                    {i18n.t('packaging.upgrade_plan.buttons.know_more')}
                  </Button>
                }
              />
            </>
          )}
          {onboarding && (
            <DefaultNotification
              onboardingStatus={onboarding.status}
              onboardingDeadline={onboarding.deadline}
              bankAccount={bankAccount}
              bankAddress={account?.bankAddress}
              locale={locale}
            />
          )}
        </Box>
        <Box alignItems="center" justifyContent="flexStart" width="full" height="full">
          <Box gap="s16" width="full">
            <Outlet />
            <CardsDataTable
              cardholders={cardholders}
              companyId={companyId}
              tableIdentifier={'GeneralFinanceCardsTable'}
              canAddCard={canAddCard}
            />
          </Box>
        </Box>
        {isOpen && !!physicalCardsAvailability?.usageLimit && (
          <UpsellingKit.ProductModal
            isOpen={isOpen}
            onClose={() => toggleOpen(false)()}
            requestSource="Cards list page"
            featureRequested={Features.expenses_physical_cards}
            moduleRequested={AvailableModules.expenses}
            requestReason={missingFeatureRequestReason(company)}
            pageSource="banking_cards_page"
          />
        )}
      </>
    )
  }

  const showHeader = ctaDescription === undefined && !onboarding?.deadline

  return (
    <ResponsivePage.Body
      showHeader={showHeader}
      variant="full"
      loading={loading}
      error={error}
      authorized={!!data?.finance?._policies.sidebarSeeFinanceCardsSubLevelItem.can}
    >
      <Content />
    </ResponsivePage.Body>
  )
}

export default CardsPage
